"use client";

import type { ReactNode } from "react";
import { Tabs, VStack } from "@chakra-ui/react";
import { MindstringPrototype } from "@/generated/fastapi";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import {
  useSituationAccessors,
  useSituationsQuery,
} from "@/features/situations";
import type { StudioLayoutMap } from "../types";
import StudioSituationSection from "./StudioSituationSection";

type StudioPrototypeTabsProps = {
  activePrototypeCategory: MindstringPrototype;
  isFreeList?: boolean;
  isLocked: boolean;
  isManualAddGuidanceActive: boolean;
  noSelectionFallback?: (props: {
    prototypeCategory: MindstringPrototype;
  }) => ReactNode;
  onPrototypeCategoryChange: (prototype: MindstringPrototype) => void;
  situationContentComponent: (props: {
    situationId: string;
    prototype: MindstringPrototype;
  }) => ReactNode;
  studioLayoutMap: StudioLayoutMap;
};

export default function StudioPrototypeTabs({
  activePrototypeCategory,
  isFreeList = false,
  isLocked,
  isManualAddGuidanceActive,
  noSelectionFallback,
  onPrototypeCategoryChange,
  situationContentComponent,
  studioLayoutMap,
}: StudioPrototypeTabsProps) {
  const situationsQuery = useSituationsQuery();
  const { getSituationById } = useSituationAccessors(situationsQuery.data);

  return (
    <Tabs.Root
      value={activePrototypeCategory}
      onValueChange={(event) => {
        onPrototypeCategoryChange(event.value as MindstringPrototype);
      }}
      variant="outline"
      fitted
    >
      {Object.entries(studioLayoutMap).map(([protoCat, situationIds]) => {
        const prototype = protoCat as MindstringPrototype;

        return (
          <Tabs.Content key={protoCat} value={protoCat}>
            {isFreeList ? (
              noSelectionFallback?.({
                prototypeCategory: prototype,
              })
            ) : (
              <VStack align="stretch" spaceY={2}>
                {situationIds.length === 0 ? (
                  noSelectionFallback ? (
                    noSelectionFallback({
                      prototypeCategory: prototype,
                    })
                  ) : (
                    <GenericEmptyComponent
                      title="Nothing selected"
                      message="Select situations in the Select view to see related options."
                      variant="inline"
                    />
                  )
                ) : (
                  situationIds.map((situationId) => {
                    const situation = getSituationById(situationId);

                    if (!situation) {
                      return null;
                    }

                    return (
                      <StudioSituationSection
                        key={situationId}
                        isLocked={isLocked}
                        isManualAddGuidanceActive={
                          isManualAddGuidanceActive
                        }
                        prototype={prototype}
                        situation={situation}
                        situationContentComponent={situationContentComponent}
                      />
                    );
                  })
                )}
              </VStack>
            )}
          </Tabs.Content>
        );
      })}
    </Tabs.Root>
  );
}
